import { forwardRef } from 'react';
import MuiButton, { buttonClasses } from '@mui/material/Button';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

export interface ButtonProps extends MuiButtonProps {
  /** Bloqueia o acionamento e sobrepõe um indicador de progresso, sem alterar a largura. */
  loading?: boolean;
}

// O diâmetro acompanha o tamanho do botão em múltiplos da unidade de spacing do
// tema (16/20/24px), em vez de pixels fixos.
const MULTIPLICADOR_DO_SPINNER: Record<NonNullable<MuiButtonProps['size']>, number> = {
  small: 2,
  medium: 2.5,
  large: 3,
};

/** Botão do design system: defaults do projeto, estado de carregamento e foco visível. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'contained',
    size = 'medium',
    color = 'primary',
    loading = false,
    disabled = false,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const theme = useTheme();

  // O MUI zera o outline do botão e só marca foco no `contained` (via elevação).
  // Sem este anel, `text` e `outlined` não dão nenhuma pista de foco de teclado.
  const anelDeFoco: SxProps<Theme> = {
    [`&.${buttonClasses.focusVisible}`]: {
      outline: `${theme.spacing(0.25)} solid ${theme.palette.text.primary}`,
      outlineOffset: theme.spacing(0.25),
    },
  };

  // Os ícones somem junto do rótulo, mas seguem ocupando espaço: é o que mantém a
  // largura igual nos dois estados.
  const conteudoEmCarregamento: SxProps<Theme> = {
    [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: { opacity: 0 },
  };

  return (
    <MuiButton
      ref={ref}
      variant={variant}
      size={size}
      color={color}
      {...rest}
      // Derivados depois do repasse: em carregamento o botão está desabilitado e
      // ocupado, e nenhuma prop do consumidor pode dizer o contrário.
      disabled={disabled || loading}
      aria-busy={loading}
      sx={[
        anelDeFoco,
        ...(loading ? [conteudoEmCarregamento] : []),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* `opacity` em vez de `visibility`: o rótulo continua na árvore de
          acessibilidade (o botão mantém nome) e sai da checagem de contraste. */}
      <span style={{ opacity: loading ? 0 : undefined }}>{children}</span>
      {loading && (
        <CircularProgress
          aria-hidden
          color="inherit"
          size={theme.spacing(MULTIPLICADOR_DO_SPINNER[size])}
          // Fora do fluxo: não entra no cálculo de largura do botão.
          // `inset: 0` + `margin: 'auto'`: o navegador centraliza a caixa do spinner no
          // espaço absoluto do botão sem depender de compensar a própria metade via
          // transform, evitando o desvio de subpixel que aparecia com top/left 50%.
          sx={{ position: 'absolute', inset: 0, margin: 'auto' }}
        />
      )}
    </MuiButton>
  );
});
